import { AxiosApiError, getApiError } from '@/types/apiError';
import { QueryCacheNotifyEvent, QueryFunction, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

type DataGridResponse<T> = {
  data: T;
};

type ReturnType<T, R> = R extends undefined ? T : R;

export const useDataGridFetcher = <
  T extends { pageData: { total: number } },
  R = undefined,
  P extends { page?: string | number; perPage?: string | number } = {
    page?: string | number;
    perPage?: string | number;
  },
>({
  queryKey,
  queryFn,
  transformResponse,
  onError,
  enabled = true,
}: {
  queryKey: (parameters?: P) => QueryKey;
  queryFn: (parameters?: P) => QueryFunction<DataGridResponse<T>>;
  transformResponse?: (data: T) => R;
  onError?: (error: Error) => void;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [rawError, setRawError] = useState<AxiosApiError>();
  const previousQueryKeyReference = useRef<string>('');
  const [resetTrigger, setResetTrigger] = useState(0);

  useEffect(() => {
    const queryKeyWithoutPagination = queryKey({
      page: undefined,
      perPage: undefined,
    } as P);
    const currentQueryKey = JSON.stringify(queryKeyWithoutPagination);

    if (previousQueryKeyReference.current && previousQueryKeyReference.current !== currentQueryKey) {
      clearError();
    }
    previousQueryKeyReference.current = currentQueryKey;

    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe((event: QueryCacheNotifyEvent & { action?: { type: string } }) => {
        if (event.action?.type === 'setState') {
          const removedQueryKey = event.query.queryKey;
          const baseQueryKey = queryKeyWithoutPagination;

          if (
            Array.isArray(removedQueryKey) &&
            Array.isArray(baseQueryKey) &&
            removedQueryKey[0] === baseQueryKey[0] &&
            removedQueryKey[1] &&
            baseQueryKey[1] &&
            typeof removedQueryKey[1] === 'object' &&
            typeof baseQueryKey[1] === 'object'
          ) {
            const removedParameters = removedQueryKey[1] as Record<string, unknown>;
            const baseParameters = baseQueryKey[1] as Record<string, unknown>;

            const isSameBaseQuery = Object.keys(baseParameters).every(key => {
              if (key === 'page' || key === 'perPage') return true;
              return removedParameters[key] === baseParameters[key];
            });

            if (isSameBaseQuery) {
              setResetTrigger(previous => previous + 1);
            }
          }
        }
      });

    return unsubscribe;
  }, [queryKey]);

  useEffect(() => {
    if (rawError && onError) {
      onError(rawError);
    }
  }, [rawError, onError]);

  const fetchData = useCallback(
    async (parameters?: P): Promise<ReturnType<T, R>> => {
      if (!enabled) {
        return {
          pageData: { total: 0 },
        } as ReturnType<T, R>;
      }

      try {
        clearError();
        const response = await queryClient.ensureQueryData({
          queryKey: queryKey(parameters),
          queryFn: queryFn(parameters),
          retry: false,
        });
        if (transformResponse) {
          return transformResponse(response.data) as ReturnType<T, R>;
        }

        return response.data as ReturnType<T, R>;
      } catch (error) {
        setRawError(error as AxiosApiError);
        throw error;
      }
    },
    [queryClient, queryKey, queryFn, transformResponse, resetTrigger, enabled]
  );

  const clearError = useCallback(() => {
    setRawError(undefined);
  }, []);

  return {
    fetchData,
    error: getApiError(rawError),
    clearError,
  };
};
